var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState } from "react";
import { Switch } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { PageContainer, PageContent } from "../../components/wraper";
import { SecondaryGrid } from "@ui/components/grid";
import { GridFilters } from "../../components/grid/GridFilters";
import PageHeader from "@ui/components/header/PageHeader";
import { SButton } from "@ui/components/buttons/SButton";
import { useRolesFilter } from "./hooks/useRolesFilter";
import { SMenuTrigger, SMenu } from "@ui/components/menu/SMenu";
import { useDispatch } from "react-redux";
import { setPagination, updateFilters } from "@reducers/roles/slice/role-slice";
import { deleteRole, getAllRoles, getRolesDropdown, } from "@reducers/roles/thunk/role-thunk";
import { toast } from "@ui/utils/toaster";
import ConfirmDialog from "@ui/components/dialog/Confirmation";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@application/store/useStore";
import { usePermissions } from "@ui/hooks/usePermissions";
import { Permission } from "@utility/permission-checker";
import "@reducers/injectReducters";
import { RoleCard } from "./components/RoleCard";
const RolesPage = () => {
    var _a, _b;
    const { t } = useTranslation(["form", "validation", "common"]);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { hasPermission } = usePermissions();
    // Simple menu state - minimal addition
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedRoleId, setSelectedRoleId] = useState(null);
    const [selectedStatusTab, setSelectedStatusTab] = useState("all");
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState(null);
    const [sortState, setSortState] = useState(null);
    const { deleteLoading, currentPageNumber, currentPageSize, roleFilters, rolesDropdown, dropdownLoading, } = useAppSelector((state) => state.role);
    // Use existing custom hook
    const { roles, loading, searchTerm, filters, paginationInfo, rolesSummary, summaryLoading, handleFilterChange, handleSearchChange, handleToggleActive, handleApplyFilters, handleClearFilters, } = useRolesFilter(sortState, selectedStatusTab);
    const handleAddRole = () => {
        navigate("/roles/new");
    };
    const handleEditRole = (roleId) => {
        navigate(`/roles/${roleId}`);
    };
    const handleDeleteRole = (roleId) => {
        setRoleToDelete(roleId);
        setDeleteDialogOpen(true);
        handleMenuClose();
    };
    const handleConfirmDelete = () => __awaiter(void 0, void 0, void 0, function* () {
        if (roleToDelete) {
            yield dispatch(deleteRole(roleToDelete))
                .unwrap()
                .then((response) => {
                toast.success(response.message || t("common:toasts.roleDeletedSuccessfully"));
                // Refresh roles list and tabs
                dispatch(getAllRoles({
                    filters: roleFilters,
                    pageNumber: currentPageNumber,
                    pageSize: currentPageSize,
                }));
                // Refresh roles summary to update tabs
                handleApplyFilters();
                // Refresh roles dropdown cache to reflect deletion
                dispatch(getRolesDropdown());
                setDeleteDialogOpen(false);
                setRoleToDelete(null);
            })
                .catch((error) => {
                toast.error(typeof error === "string"
                    ? error
                    : t("common:toasts.failedToDeleteRole"));
            });
        }
    });
    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setRoleToDelete(null);
    };
    const handleMenuClick = (event, roleId) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
        setSelectedRoleId(roleId);
    };
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setSelectedRoleId(null);
    };
    // Handle status tab change to filter by Active/Inactive/All
    const handleStatusTabChange = (tabValue) => {
        setSelectedStatusTab(tabValue);
        if (tabValue === "all") {
            dispatch(updateFilters({ isActive: undefined }));
        }
        else if (tabValue === "active") {
            dispatch(updateFilters({ isActive: true }));
        }
        else if (tabValue === "inactive") {
            dispatch(updateFilters({ isActive: false }));
        }
    };
    // Generate status-based tabs (Active/Inactive/All)
    const statusTabs = React.useMemo(() => {
        const totalCount = (paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCount) || 0;
        const activeCount = roles.filter((r) => r.isActive).length;
        const inactiveCount = roles.filter((r) => !r.isActive).length;
        return [
            {
                label: `${t("common:tabs.all")}`,
                value: "all",
            },
            {
                label: `${t("common:tabs.active")}`,
                value: "active",
            },
            {
                label: `${t("common:tabs.inactive")}`,
                value: "inactive",
            },
        ];
    }, [roles, paginationInfo]);
    // Fetch roles dropdown on mount for filter - only if not already loaded
    React.useEffect(() => {
        if (!rolesDropdown && !dropdownLoading) {
            dispatch(getRolesDropdown());
        }
    }, [dispatch, rolesDropdown, dropdownLoading]);
    // Menu items configuration - filtered by permissions
    const menuItems = selectedRoleId
        ? [
            ...(hasPermission(Permission.ROLES_EDIT)
                ? [
                    {
                        label: t("common:actions.edit"),
                        onClick: () => {
                            handleEditRole(selectedRoleId);
                            handleMenuClose();
                        },
                        disabled: ((_a = roles === null || roles === void 0 ? void 0 : roles.find((r) => r.id === selectedRoleId)) === null || _a === void 0 ? void 0 : _a.isSystemRole) ||
                            false,
                    },
                ]
                : []),
            ...(hasPermission(Permission.ROLES_DELETE)
                ? [
                    {
                        label: t("common:actions.delete"),
                        onClick: () => {
                            handleDeleteRole(selectedRoleId);
                            handleMenuClose();
                        },
                        color: "error",
                    },
                ]
                : []),
        ]
        : [];
    const handleSortChange = (field, order) => {
        setSortState({ field, order });
    };
    const columns = [
        {
            field: "name",
            headerName: t("common:grid.role"),
            flex: 2,
            minWidth: 150,
            filterable: true,
            filterType: "select",
            filterOptions: (rolesDropdown === null || rolesDropdown === void 0 ? void 0 : rolesDropdown.map((r) => r.name)) || [],
            sortable: true,
        },
        {
            field: "assignedUsersCount",
            headerName: t("common:grid.users"),
            flex: 2,
            minWidth: 150,
            filterable: true,
            filterType: "select",
            filterOptions: (rolesDropdown === null || rolesDropdown === void 0 ? void 0 : rolesDropdown.map((r) => r.name)) || [],
            sortable: true,
        },
        {
            field: "isActive",
            headerName: t("common:grid.active"),
            flex: 1,
            minWidth: 100,
            filterable: false,
            renderCell: (params) => (React.createElement(Switch, { checked: params.row.isActive !== false, onChange: () => handleToggleActive(params.row), disabled: !hasPermission(Permission.ROLES_EDIT) || params.row.isSystemRole, size: "small" })),
        },
        ...(hasPermission(Permission.ROLES_EDIT) ||
            hasPermission(Permission.ROLES_DELETE)
            ? [
                {
                    field: "actions",
                    headerName: t("common:grid.actions"),
                    minWidth: 80,
                    filterable: false,
                    renderCell: (params) => (React.createElement(SMenuTrigger, { size: "medium", onClick: (event) => handleMenuClick(event, params.row.id) })),
                },
            ]
            : []),
    ];
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: t("common:pages.rolesPermissions"), topActions: hasPermission(Permission.ROLES_CREATE) ? (React.createElement(SButton, { variant: "contained", size: "large", onClick: handleAddRole }, t("common:actions.addRole"))) : undefined }),
        React.createElement(PageContent, { sx: { pb: 0 } },
            React.createElement(GridFilters, { title: `${(_b = paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCount) !== null && _b !== void 0 ? _b : roles.length} ${t("common:pages.roles")}`, loading: loading, columns: [], filters: filters, onChange: handleFilterChange, searchProp: searchTerm, handleSearchChange: handleSearchChange, tabs: statusTabs, selectedTab: selectedStatusTab, onTabChange: handleStatusTabChange, onApplyFilters: handleApplyFilters, onClearFilters: handleClearFilters, sortState: sortState, onSortChange: handleSortChange, hideFilter: true }),
            React.createElement(SecondaryGrid, { rows: loading ? [] : roles, columns: columns, loading: loading, paginationMode: "server", rowCount: (paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCount) || 0, paginationModel: {
                    page: ((paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.pageNumber) || 1) - 1,
                    pageSize: (paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.pageSize) || 10,
                }, onPaginationModelChange: (model) => {
                    const newPageNumber = model.page + 1;
                    const newPageSize = model.pageSize;
                    if (newPageNumber !== (paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.pageNumber) ||
                        newPageSize !== (paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.pageSize)) {
                        dispatch(setPagination({
                            pageNumber: newPageNumber,
                            pageSize: newPageSize,
                        }));
                    }
                }, pageSizeOptions: [25, 50], renderCard: (row) => (React.createElement(RoleCard, { row: row, onMenuClick: handleMenuClick, onToggleActive: handleToggleActive, canEdit: hasPermission(Permission.ROLES_EDIT) })) }),
            React.createElement(SMenu, { anchorEl: menuAnchorEl, open: Boolean(menuAnchorEl), handleClose: handleMenuClose, menuItems: menuItems }),
            React.createElement(ConfirmDialog, { isOpen: deleteDialogOpen, onClose: handleCloseDeleteDialog, onConfirm: handleConfirmDelete, loading: deleteLoading, actionText: t("common:actions.delete"), screenName: t("common:labels.role"), variant: "delete" }))));
};
export default RolesPage;
